import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router';
import { UserProvider } from './lib/userContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import * as Sentry from '@sentry/react';
import { SentryErrorFallback } from './components/SentryErrorFallback';
import { Layout } from './components/Layout';
import { enforceHttps, checkSecurityHeaders, preventClickjacking } from './lib/security';
import { registerServiceWorker } from './lib/serviceWorker';

// Lazy load page components for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard'));
const CreateReport = lazy(() => import('./pages/CreateReport'));
const ReviewReport = lazy(() => import('./pages/ReviewReport'));
const Archive = lazy(() => import('./pages/Archive'));
const ManageProjects = lazy(() => import('./pages/ManageProjects'));
const ManageUsers = lazy(() => import('./pages/ManageUsers'));
const Login = lazy(() => import('./pages/Login'));

const NotFound = lazy(() => import('./pages/NotFound'));

// Loading fallback component - defined outside App to prevent remounts
export function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
        <p className="text-neutral-600 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function LazyWrapper({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

export default function App() {
  // Security enforcement and PWA registration - runs once on mount
  useEffect(() => {
    enforceHttps();
    checkSecurityHeaders();
    preventClickjacking();
    
    // Register service worker for PWA support
    if (import.meta.env.PROD) {
      registerServiceWorker().catch(console.error);
    }
  }, []);
  
  return (
    <Sentry.ErrorBoundary fallback={<SentryErrorFallback />} showDialog>
      <UserProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LazyWrapper><Login /></LazyWrapper>} />

            {/* Protected routes - require authentication */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* All authenticated users can access dashboard */}
              <Route index element={<LazyWrapper><Dashboard /></LazyWrapper>} />
              
              {/* All authenticated users can create reports */}
              <Route path="create" element={<LazyWrapper><CreateReport /></LazyWrapper>} />
              
              {/* All authenticated users can access archive */}
              <Route path="archive" element={<LazyWrapper><Archive /></LazyWrapper>} />
              
              {/* Manager-only routes */}
              <Route 
                path="review/:id" 
                element={
                  <ProtectedRoute allowedRoles={['manager']}>
                    <LazyWrapper><ReviewReport /></LazyWrapper>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="projects" 
                element={
                  <ProtectedRoute allowedRoles={['manager']}>
                    <LazyWrapper><ManageProjects /></LazyWrapper>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="users" 
                element={
                  <ProtectedRoute allowedRoles={['manager']}>
                    <LazyWrapper><ManageUsers /></LazyWrapper>
                  </ProtectedRoute>
                } 
              />
              
              {/* 404 within dashboard */}
              <Route path="*" element={<LazyWrapper><NotFound /></LazyWrapper>} />
            </Route>
            
            {/* Root redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            
            {/* Catch all - 404 */}
            <Route path="*" element={<LazyWrapper><NotFound /></LazyWrapper>} />
          </Routes>
        </BrowserRouter>
      </UserProvider>
    </Sentry.ErrorBoundary>
  );
}
